import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Star,
  Search,
  CheckCircle2,
  EyeOff,
  Trash2,
  Loader2,
  ListChecks,
  MessageSquareWarning,
} from 'lucide-react';
import {
  useAdminReviews,
  useAdminReviewStatistics,
  useApproveAdminReview,
  useHideAdminReview,
  useDeleteAdminReview,
  useBulkApproveAdminReviews,
} from '../../services/adminReviewService';
import { AdminPageHeader, AdminGuideCallout, AdminEmptyState } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { useToastContext } from '../../contexts/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import Button from '../../components/shared/Button';

const STATUS_PILLS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Published' },
  { value: 'all', label: 'All' },
];

const RATING_PILLS = [
  { value: '', label: 'Any ★' },
  { value: '5', label: '5★' },
  { value: '4', label: '4★' },
  { value: '3', label: '3★' },
  { value: '2', label: '2★' },
  { value: '1', label: '1★' },
];

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${
          i <= Number(rating || 0)
            ? 'fill-amber-400 text-amber-400'
            : 'text-gray-300 dark:text-gray-600'
        }`}
      />
    ))}
  </div>
);

const AdminReviews = () => {
  const { success, error: showError } = useToastContext();
  const [status, setStatus] = useState('pending');
  const [rating, setRating] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);

  const params = useMemo(
    () => ({
      status,
      rating: rating || undefined,
      search: search || undefined,
      page,
      per_page: 20,
      sort: 'newest',
    }),
    [status, rating, search, page]
  );

  const { data, isLoading, error } = useAdminReviews(params);
  const { data: statsData } = useAdminReviewStatistics();
  const stats = statsData?.data;

  const approveMutation = useApproveAdminReview({
    onSuccess: () => success('Review published'),
    onError: (err) => showError(err?.response?.data?.message || 'Approve failed'),
  });
  const hideMutation = useHideAdminReview({
    onSuccess: () => success('Review hidden'),
    onError: (err) => showError(err?.response?.data?.message || 'Hide failed'),
  });
  const deleteMutation = useDeleteAdminReview({
    onSuccess: () => success('Review deleted'),
    onError: (err) => showError(err?.response?.data?.message || 'Delete failed'),
  });
  const bulkApproveMutation = useBulkApproveAdminReviews({
    onSuccess: (res) => {
      success(res?.message || 'Reviews approved');
      setSelected([]);
    },
    onError: (err) => showError(err?.response?.data?.message || 'Bulk approve failed'),
  });

  const paginator = data?.data;
  const reviews = paginator?.data || [];

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllPending = () => {
    const pendingIds = reviews.filter((r) => !r.is_approved).map((r) => r.id);
    if (pendingIds.length === 0) return;
    const allSelected = pendingIds.every((id) => selected.includes(id));
    setSelected(allSelected ? selected.filter((id) => !pendingIds.includes(id)) : [
      ...new Set([...selected, ...pendingIds]),
    ]);
  };

  const handleDelete = (review) => {
    if (
      !window.confirm(
        `Permanently delete review from ${review.client?.name || 'client'}? This cannot be undone.`
      )
    ) {
      return;
    }
    deleteMutation.mutate(review.id);
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Reviews moderation"
        subtitle="Approve, hide, or remove client reviews before or after they appear on vendor profiles."
        actions={
          selected.length > 0 ? (
            <Button
              className="!rounded-xl whitespace-nowrap"
              onClick={() => bulkApproveMutation.mutate(selected)}
              disabled={bulkApproveMutation.isPending}
            >
              {bulkApproveMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
              )}
              Approve selected ({selected.length})
            </Button>
          ) : null
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.reviews.title}
        steps={ADMIN_PAGE_GUIDES.reviews.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Pending', value: stats?.pending ?? '—', tone: 'amber' },
          { label: 'Published', value: stats?.approved ?? '—', tone: 'emerald' },
          { label: 'Total', value: stats?.total ?? '—', tone: 'slate' },
          {
            label: 'Avg rating',
            value: stats?.average_rating != null ? Number(stats.average_rating).toFixed(1) : '—',
            tone: 'sky',
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 p-4 shadow-sm"
          >
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{card.label}</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-50">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
          >
            {STATUS_PILLS.map((pill) => {
              const active = status === pill.value;
              const count =
                pill.value === 'pending'
                  ? stats?.pending
                  : pill.value === 'approved'
                    ? stats?.approved
                    : stats?.total;
              return (
                <button
                  key={pill.value}
                  type="button"
                  onClick={() => {
                    setStatus(pill.value);
                    setPage(1);
                    setSelected([]);
                  }}
                  className={`px-3 py-1.5 text-sm font-medium !rounded-lg transition-colors ${
                    active
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-50'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
                  }`}
                >
                  {pill.label}
                  {count != null && (
                    <span className="ml-1.5 tabular-nums text-xs text-gray-400">{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
          >
            {RATING_PILLS.map((pill) => {
              const active = rating === pill.value;
              return (
                <button
                  key={pill.value || 'any'}
                  type="button"
                  onClick={() => {
                    setRating(pill.value);
                    setPage(1);
                  }}
                  className={`px-2.5 py-1.5 text-xs font-medium !rounded-lg transition-colors ${
                    active
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-50'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search client, vendor, title, booking…"
            className="w-full pl-10 pr-3 py-2.5 text-sm !rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 py-16 flex items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
          Loading reviews…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          Could not load reviews.
        </div>
      ) : reviews.length === 0 ? (
        <AdminEmptyState
          icon={MessageSquareWarning}
          title={status === 'pending' ? 'No pending reviews' : 'No reviews found'}
          description={
            status === 'pending'
              ? 'New client reviews will appear here until you publish them.'
              : 'Try another status, rating, or search term.'
          }
        />
      ) : (
        <div className="space-y-3">
          {status === 'pending' && reviews.some((r) => !r.is_approved) && (
            <div className="flex items-center justify-between px-1">
              <button
                type="button"
                onClick={toggleSelectAllPending}
                className="text-xs font-medium text-sky-700 hover:text-sky-900 dark:text-sky-400"
              >
                {reviews.filter((r) => !r.is_approved).every((r) => selected.includes(r.id))
                  ? 'Clear selection'
                  : 'Select all pending on this page'}
              </button>
            </div>
          )}

          {reviews.map((review) => {
            const vendorUserId = review.vendor?.user_id;
            return (
              <article
                key={review.id}
                className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 p-4 sm:p-5 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  {!review.is_approved && (
                    <input
                      type="checkbox"
                      checked={selected.includes(review.id)}
                      onChange={() => toggleSelect(review.id)}
                      className="mt-1.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label="Select review"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Stars rating={review.rating} />
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          review.is_approved
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50'
                            : 'bg-amber-50 text-amber-800 border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50'
                        }`}
                      >
                        {review.is_approved ? 'Published' : 'Pending'}
                      </span>
                      {review.is_verified && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-800 border border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300">
                          Verified booking
                        </span>
                      )}
                    </div>

                    {review.title && (
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 mb-1">
                        {review.title}
                      </h3>
                    )}
                    {review.comment && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                        {review.comment}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-4">
                      <span>
                        Client:{' '}
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {review.client?.name || '—'}
                        </span>
                      </span>
                      <span>
                        Vendor:{' '}
                        {vendorUserId ? (
                          <Link
                            to={`/admin/vendors/${vendorUserId}`}
                            className="font-medium text-primary-700 dark:text-primary-300 hover:underline"
                          >
                            {review.vendor?.business_name || 'Vendor'}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {review.vendor?.business_name || '—'}
                          </span>
                        )}
                      </span>
                      {review.booking?.booking_reference && (
                        <span>
                          Booking:{' '}
                          <Link
                            to={`/admin/bookings/${review.booking_id || review.booking.id}`}
                            className="font-medium text-primary-700 dark:text-primary-300 hover:underline"
                          >
                            {review.booking.booking_reference}
                          </Link>
                        </span>
                      )}
                      <span>{review.created_at ? formatDate(review.created_at, 'short') : ''}</span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {!review.is_approved ? (
                        <Button
                          size="sm"
                          className="!rounded-xl"
                          onClick={() => approveMutation.mutate(review.id)}
                          disabled={approveMutation.isPending}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                          Publish
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="!rounded-xl"
                          onClick={() => hideMutation.mutate(review.id)}
                          disabled={hideMutation.isPending}
                        >
                          <EyeOff className="w-3.5 h-3.5 mr-1.5" />
                          Hide
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="!rounded-xl text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/30"
                        onClick={() => handleDelete(review)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}

          {paginator?.last_page > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-500">
                Page {paginator.current_page} of {paginator.last_page}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-xs font-medium !rounded-xl border border-gray-200 dark:border-gray-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= paginator.last_page}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium !rounded-xl border border-gray-200 dark:border-gray-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
